import React from 'react';
import { Box, Link, Typography, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { PieChart } from '@mui/x-charts/PieChart';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BalanceBucketsDto } from '../../../../common/interfaces/invoicing.interface';
import { FINANCE_DEBTS } from '../../../../common/constants/routes.constants';
import { formatMoney } from '../../../../utilities/helper/money.helper';
import { groupQuery, KINDERGARTEN_SCOPE, OverviewScope } from './scope';

export const cardSx = { border: 1, borderColor: 'line', borderRadius: 4, p: 3, minWidth: 0 } as const;

/**
 * Today's debts by how late they are, in a ring: more than 30 days, up to 30 days, not due
 * yet. Each row under it opens the debts page on that card.
 */
const DebtDonut: React.FC<{ buckets: BalanceBucketsDto | null; kindergartenId: string; scope?: OverviewScope }> = ({
  buckets,
  kindergartenId,
  scope = KINDERGARTEN_SCOPE
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const parts = [
    { bucket: 'late30' as const, color: theme.palette.error.main, label: t('balances.cards.late30') },
    { bucket: 'late' as const, color: theme.palette.warning.main, label: t('balances.cards.late') },
    { bucket: 'notdue' as const, color: theme.palette.field.disabled, label: t('balances.cards.notdue') }
  ].map(part => ({ ...part, ...(buckets?.[part.bucket] ?? { amount: 0, count: 0 }) }));
  const total = buckets?.all.amount ?? 0;
  const href = (bucket?: string) =>
    `${FINANCE_DEBTS}?kindergartenId=${kindergartenId}${bucket ? `&bucket=${bucket}` : ''}${groupQuery(scope)}`;
  const share = (amount: number) => (total > 0 ? Math.round((amount / total) * 100) : 0);

  return (
    <Box component="section" aria-labelledby="debt-donut-title" sx={cardSx}>
      <Typography id="debt-donut-title" component="h2" sx={{ fontSize: 16, fontWeight: 600, mb: 2 }}>
        {t('finance.debtDonut.title')}
      </Typography>
      {total <= 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4 }}>
          <CheckIcon aria-hidden sx={{ color: 'success.main' }} />
          <Typography sx={{ fontSize: 15 }}>{t('balances.empty.all')}</Typography>
        </Box>
      ) : (
        <>
          <Box
            role="img"
            aria-label={t('finance.debtDonut.aria', {
              parts: parts.map(part => `${part.label} ${share(part.amount)} %`).join(', ')
            })}
            sx={{ position: 'relative', width: 148, height: 148, mx: 'auto', mb: 2 }}
          >
            <PieChart
              width={148}
              height={148}
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              series={[
                {
                  data: parts.map(part => ({ id: part.bucket, value: part.amount, color: part.color })),
                  innerRadius: 52,
                  outerRadius: 74,
                  paddingAngle: 1
                }
              ]}
              slotProps={{ legend: { hidden: true } }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none'
              }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatMoney(total)}</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {t('finance.debtDonut.center', { count: buckets?.all.count ?? 0 })}
              </Typography>
            </Box>
          </Box>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {parts.map(part => (
              <Box component="li" key={part.bucket}>
                <Link
                  component={RouterLink}
                  to={href(part.bucket)}
                  underline="hover"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, color: 'text.primary', fontSize: 14 }}
                >
                  <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: part.color, flexShrink: 0 }} />
                  <Box component="span" sx={{ flex: 1 }}>
                    {part.label}
                  </Box>
                  <Box component="span" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(part.amount)}
                  </Box>
                  <Box component="span" sx={{ color: 'text.secondary', minWidth: 28, textAlign: 'right' }}>
                    · {part.count}
                  </Box>
                </Link>
              </Box>
            ))}
          </Box>
        </>
      )}
      <Link
        component={RouterLink}
        to={href()}
        underline="hover"
        sx={{ display: 'inline-block', mt: 1.5, fontSize: 14, color: 'primary.main' }}
      >
        {scope.kind === 'kindergarten' ? t('finance.debtDonut.open') : t('finance.debtDonut.openFiltered')} →
      </Link>
    </Box>
  );
};

export default DebtDonut;
